import { ContentDetailSummary } from '@raydiant/api-client-js';
import { BarChart, LineChart } from 'echarts/charts';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { useTranslation } from 'react-i18next';
import Hidden from 'raydiant-elements/layout/Hidden';
import { renderToStaticMarkup } from 'react-dom/server';
import { Popover } from '@headlessui/react';

import {
  ageGroups,
  averageDataLineColor,
  colorsForContentDetailCharts,
  getVerifiedViewRateData,
  iconForLegend,
} from './utils';
import { CHART_DEFAULT_AXIS_OPTIONS, CHART_DEFAULT_OPTIONS } from '../utils';
import ChartToolTip, { EChartsToolTipParams } from '../ChartToolTip';
import ChartCard from '../ChartCard/ChartCard';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import config from '../../../config';

const { supportAppUrl } = config;

echarts.use([BarChart, LineChart]);

const color = [...colorsForContentDetailCharts, averageDataLineColor];

type VerifiedViewRateChartProps = {
  contentData: ContentDetailSummary;
};

export default function VerifiedViewRateChart({
  contentData,
}: VerifiedViewRateChartProps) {
  const { t } = useTranslation();
  const { male, female } = getVerifiedViewRateData(contentData);

  const maleData = male.map((value) => Number(value).toFixed(2));
  const femaleData = female.map((value) => Number(value).toFixed(2));
  const averageContent = Number(contentData.allAvgVerifiedViewsRate).toFixed(2);
  const viewRateMalePercentage = Number(
    contentData.verifiedViewRateMale,
  ).toFixed(2);
  const viewRateFemalePercentage = Number(
    contentData.verifiedViewRateFemale,
  ).toFixed(2);
  const isEmptyData =
    !Number(viewRateMalePercentage) && !Number(viewRateFemalePercentage);

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    grid: {
      ...CHART_DEFAULT_OPTIONS.grid,
      top: 50,
    },
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: EChartsToolTipParams[]) => {
        const modifiedParams: EChartsToolTipParams[] = params.map((item) => ({
          ...item,
          value: `${item.value}%`,
        }));

        modifiedParams.push({
          seriesName: t(
            'analytics.contentDetailPage.verifiedViewRate.averageContent',
          ),
          value: `${averageContent}%`,
          color: averageDataLineColor,
          borderColor: '',
          dataIndex: 0,
          name: '',
          data: [],
        });

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 text-black">
                {params[0].axisValueLabel}
              </p>
            }
            values={modifiedParams}
          />,
        );
      },
    },
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      icon: iconForLegend,
    },
    color: color,
    xAxis: {
      ...CHART_DEFAULT_AXIS_OPTIONS.xAxis,
      type: 'value',
      name: t(
        'analytics.contentDetailPage.verifiedViewRate.xAxisTitle',
      ).toUpperCase(),
      axisLabel: {
        ...CHART_DEFAULT_AXIS_OPTIONS.xAxis.axisLabel,
        formatter: (value: string) => `${value}%`,
      },
    },
    yAxis: {
      ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
      type: 'category',
      data: ageGroups,
      name: t(
        'analytics.contentDetailPage.verifiedViewRate.yAxisTitle',
      ).toUpperCase(),
      axisLabel: {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis.axisLabel,
        alignTicks: true,
        formatter: (value: string) => value,
      },
      nameGap: 40,
    },
    series: [
      {
        name: `Male (${viewRateMalePercentage}%)`,
        type: 'bar',
        emphasis: {
          focus: 'series',
        },
        data: maleData,
      },
      {
        name: `Female (${viewRateFemalePercentage}%)`,
        type: 'bar',
        emphasis: {
          focus: 'series',
        },
        data: femaleData,
      },
      {
        type: 'line',
        markLine: {
          symbol: 'none',
          silent: true,
          data: [
            {
              xAxis: averageContent,
              label: {
                formatter: t(
                  'analytics.contentDetailPage.verifiedViewRate.averageContent',
                ),
                color: averageDataLineColor,
              },
            },
          ],
        },
      },
    ],
  };

  const mobileOptions = {
    ...options,
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      icon: iconForLegend,
      type: 'scroll',
    },
  };

  return (
    <ChartCard
      subTitle={''}
      title={
        <div className="flex gap-3 items-center">
          <h2 className="text-xl font-bold text-black">
            {t('analytics.contentDetailPage.verifiedViewRate.title')}
          </h2>
          <Popover className="relative">
            <Popover.Button>
              <QuestionMarkIcon />
            </Popover.Button>
            <Popover.Panel className="absolute z-10 card popover">
              <div className="overflow-hidden">
                <p className="mb-3">
                  {t('analytics.contentDetailPage.verifiedViewRate.tooltip')}
                </p>
                <a
                  href={`${supportAppUrl}/visitor-insight/5imknKqrccCD9gmD6gKuJ1/how-do-i-use-the-insight-analytics-platform/6RjNYsyCvxraPhuSNUMYQo`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('analytics.learnmore')}
                </a>
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      }
      showTimeInterval={false}
    >
      <div className="min-w-full min-h-full">
        {isEmptyData ? (
          <div className="w-full text-center">
            {t('analytics.contentDetailPage.emptyData.emptyChartData')}
          </div>
        ) : (
          <>
            <Hidden xsDown>
              <ReactECharts
                style={{ height: '460px' }}
                option={options}
                onChartReady={(chart) => chart.resize()}
              />
            </Hidden>
            <Hidden smUp>
              <ReactECharts
                style={{ height: '460px' }}
                option={mobileOptions}
                onChartReady={(chart) => chart.resize()}
              />
            </Hidden>
          </>
        )}
      </div>
    </ChartCard>
  );
}
